import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { api } from '../lib/api'
import { Dialog, DialogContent } from './ui/dialog'
import { Button } from './ui/button'

/** StarredView lists every starred message across every conversation — the
 *  messenger "saved messages" idiom, independent of which chat is open. */
export function StarredView({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate()
  const { data: messages = [], isLoading } = useQuery({
    queryKey: ['starred'],
    queryFn: api.listStarred,
    enabled: open,
  })

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent
        showCloseButton={false}
        className="flex h-full max-h-full w-full max-w-full flex-col gap-0 rounded-none p-0 sm:top-[10%] sm:h-auto sm:max-h-[70vh] sm:w-[32rem] sm:max-w-[32rem] sm:translate-y-0 sm:rounded-lg"
        data-testid="starred-overlay"
      >
        <div className="flex items-center gap-2 border-b p-3">
          {/* Same reasoning as SearchOverlay: full-screen on a phone means
              the backdrop is unreachable, so this is the only way back. */}
          <Button
            className="inline-flex sm:hidden"
            variant="ghost"
            size="icon-sm"
            aria-label="Close starred messages"
            onClick={onClose}
            data-testid="starred-close"
          >
            ←
          </Button>
          <p className="font-semibold">⭐ Starred messages</p>
        </div>

        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="flex justify-center py-6">
              <div className="size-4 animate-spin rounded-full border-2 border-muted-foreground/30 border-t-foreground" />
            </div>
          ) : messages.length === 0 ? (
            <p className="p-4 text-center text-sm text-muted-foreground">
              Star a message to find it here later.
            </p>
          ) : (
            messages.map((m) => (
              <button
                key={m.id}
                type="button"
                className="block w-full px-4 py-3 text-left hover:bg-muted"
                onClick={() => {
                  onClose()
                  navigate(`/c/${m.conversation_id}`)
                }}
                data-testid="starred-message"
              >
                <p className="line-clamp-3 text-sm">
                  {m.parts?.find((p) => p.kind === 'text')?.text ?? ''}
                </p>
              </button>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
